import { useQuery } from '@tanstack/react-query';

import { useBrandKits } from '@/hooks/useBrandKits';
import { supabase } from '@/integrations/supabase/client';
import { isSessionUuid } from '@/lib/chat/is-session-uuid';

/**
 * Fetches only the `title` field of an `ai_sessions` row by ID.
 * Used by PageHeaderManager to populate the /create breadcrumb for
 * non-plan conversations (discover flow, resume by session id, etc.).
 *
 * Returns null when sessionId is absent, the row doesn't exist, or the
 * title hasn't been set yet by the agent (returns null from DB).
 */
export function useAiSessionTitle(sessionId: string | null | undefined): string | null {
  const { activeKit, isActiveKitPending } = useBrandKits();
  const activeKitId = activeKit?.id ?? null;

  const { data } = useQuery<string | null>({
    queryKey: ['ai-session-title', sessionId ?? null, activeKitId],
    enabled: Boolean(isSessionUuid(sessionId) && activeKitId && !isActiveKitPending),
    staleTime: 15_000,
    gcTime: 60_000,
    queryFn: async (): Promise<string | null> => {
      if (!sessionId || !activeKitId) return null;
      const { data, error } = await supabase
        .from('ai_sessions')
        .select('title')
        .eq('id', sessionId)
        .eq('brand_kit_id', activeKitId)
        .maybeSingle();
      if (error) return null;
      return data?.title ?? null;
    },
  });
  return data ?? null;
}
